import { T } from 'gt-react';
import type { NeedInventoryRow, NeedSalesPoint, ForecastPoint } from '@erp/rpc';
import { PopularityCode } from 'src/components/procurement/PopularityCell';
import { SalesHistoryChart } from 'src/components/procurement/SalesHistoryChart';
import { InventoryByWarehouseTable } from './InventoryByWarehouseTable';

export interface NeedInsightsViewProps {
  popularityCode: string | null;
  inventoryByWarehouse: NeedInventoryRow[];
  salesHistory: NeedSalesPoint[];
  forecast: ForecastPoint[];
}

// Presentational buyer's-menu panel: popularity badge, sales+forecast chart, and
// per-warehouse inventory table. Pure props — fetching lives in the wrappers
// (NeedInsightsPanel keyed by suggestion, ProductInsightsPanel keyed by product).
export function NeedInsightsView({
  popularityCode,
  inventoryByWarehouse,
  salesHistory,
  forecast,
}: NeedInsightsViewProps) {
  const totalOnHand = inventoryByWarehouse.reduce(
    (sum, w) => sum + w.onHandQuantity,
    0,
  );
  const totalAvailable = inventoryByWarehouse.reduce(
    (sum, w) => sum + w.availableQuantity,
    0,
  );

  return (
    // Let the parent column own the scroll; this panel just stacks vertically.
    <div className="flex flex-col gap-4 p-3">
      {/* ── Popularity ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-gray-500">
          <T context="pop label">Pop</T>
        </span>
        {/* Shared renderer: plain text (not a chip) because the code is a loose
            AS400 string with no validated vocabulary, and it normalizes blank
            codes to `—`. See components/procurement/PopularityCell.tsx. */}
        <PopularityCode code={popularityCode} />
      </div>

      {/* ── Sales history + forward forecast at destination ─────────────── */}
      <SalesHistoryChart salesHistory={salesHistory} forecast={forecast} />

      {/* ── Inventory across warehouses ─────────────────────────────────── */}
      <div className="flex-1">
        <div className="mb-1 flex items-baseline justify-between">
          <span className="text-xs font-medium text-gray-500">
            <T context="inventory across warehouses heading">
              Inventory across warehouses
            </T>
          </span>
          <span className="text-xs text-gray-500">
            {`${totalOnHand.toLocaleString()} on-hand · ${totalAvailable.toLocaleString()} available`}
          </span>
        </div>
        <InventoryByWarehouseTable rows={inventoryByWarehouse} />
      </div>
    </div>
  );
}
